import { Delete } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import type { ICompetenceLevel } from '@sharedInterfaces/ICompetence';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CompetenceOverlay from '@src/App/Components/Overlays/CompetenceOverlay/CompetenceOverlay';
import type { AppState } from '@store/store';
import { ShieldUser } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ItemBadge, { NewBadge } from '../../ItemBadge/ItemBadge';
import Item from '../Item/Item';

interface CompetenceItemProps {
    title: string;
    id: number;
    level?: number;
    disableOverlay?: boolean;
    onClick?: string | (() => void);
    newPrefix?: true;
    onDelete?: () => void;
}
/**
 * Represents a competence item.
 *
 * @param {string} props.title - The title of the competence item.
 * @param {string} props.id - The id of the competence item.
 * @param {number} props.level - The level of the competence item.
 * @param {boolean} props.disableOverlay - Indicates if the overlay is disabled for the competence item.
 * @param {function} props.onClick - The function to be called when the competence item is clicked.
 * @param {function} props.onDelete - The function to be called when the competence item is deleted.
 *
 * @returns {JSX.Element} The competence item as a JSX element.
 */
function CompetenceItem({ title, id, level, disableOverlay, newPrefix, onClick, onDelete }: CompetenceItemProps) {
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).competence;
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const allCompetence = allCompetences.find((c) => c.id === id);
    const [overlayOpen, setOverlayOpen] = React.useState(false);
    let percent = 75;
    let targetLevel: undefined | ICompetenceLevel;
    let levelTitle: string = '';
    let levelTitleHelper: string = '';
    if (level !== undefined && allCompetence) {
        if (level > 0) {
            percent = (level / allCompetence.levels.length) * 100;
            targetLevel = allCompetence.levels[level - 1];
            levelTitle = level && allCompetence && targetLevel ? ` - ${targetLevel.title}` : '';
            levelTitleHelper = langStrings.oftenUsed.level + (targetLevel ? `: ${targetLevel.title}` : '');
        } else {
            percent = 0;
            levelTitle = ` - ${langStrings.inPlanningShort}`;
            levelTitleHelper = langStrings.inPlanning;
        }
    }

    const handleSingleClick = () => {
        if (id && !disableOverlay) {
            setOverlayOpen(true);
        }
    };

    const handleDoubleClick = () => {
        if (onClick) {
            if (typeof onClick === 'string') {
                navigate(onClick);
            } else {
                onClick();
            }
        }
    };

    return (
        <Item
            onClick={handleSingleClick}
            onDoubleClick={handleDoubleClick}
            percent={percent}
            level={level && level >= 3 ? level : undefined} // Use actual level for contrast
            // order={level ? level * -1 : undefined}
            leftContent={
                level !== undefined && (
                    <ItemBadge value={level} title={levelTitleHelper} icon={<ShieldUser size={16} />} />
                )
            }
            rightContent={
                onDelete !== undefined && (
                    <div className="competenceItemDelete" title={langStrings.delete}>
                        <IconButton onClick={onDelete} size="small">
                            <Delete />
                        </IconButton>
                    </div>
                )
            }
        >
            <CompetenceOverlay
                key={id}
                disabled={false}
                competenceId={id}
                open={overlayOpen}
                onClose={() => setOverlayOpen(false)}
            >
                {title}
                {levelTitle}
            </CompetenceOverlay>
            {newPrefix && <NewBadge />}
        </Item>
    );
}
export default CompetenceItem;
